import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
    Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { updateContractParams } from 'services/requests/contract';
import { Formik, Form } from 'formik';
import * as yup from 'yup';

function prepareRequestBody(values) {
    return {
        updateContractParams: [
            {
                action: 'latePaymentInterest',
                value: values.latePaymentInterest
            },
            {
                action: 'latePaymentFine',
                value: values.latePaymentFine
            },
            {
                action: 'installmentDelayDays',
                value: values.installmentDelayDays
            }
        ]
    }
}

export default function EditContract({ open, onClose, contractData, onSuccess }) {
    const validationSchema = yup.object({
        latePaymentInterest: yup
            .number()
            .min(0, 'Juros de mora deve ser maior ou igual a 0')
            .max(100, 'Juros de mora deve ser menor ou igual a 100')
            .required('Juros de mora é obrigatório'),
        latePaymentFine: yup
            .number()
            .min(0, 'Multa de mora deve ser maior ou igual a 0')
            .max(100, 'Multa de mora deve ser menor ou igual a 100')
            .required('Multa de mora é obrigatório'),
        installmentDelayDays: yup
            .number()
            .min(0, 'Dias de carência deve ser maior ou igual a 0')
            .max(30, 'Dias de carência deve ser menor ou igual a 30')
            .required('Dias de carência é obrigatório')
    });

    const getInitialValues = () => {
        if (!contractData || !open) {
            return {
                latePaymentInterest: 0,
                latePaymentFine: 0,
                installmentDelayDays: 0
            };
        }

        return {
            latePaymentInterest: contractData.latePaymentInterest,
            latePaymentFine: contractData.latePaymentFine,
            installmentDelayDays: contractData.installmentDelayDays
        };
    };

    const handleFormSubmit = async (values, actions) => {
        try {
            const body = prepareRequestBody(values);
            const id = contractData.id;
            await updateContractParams({ id, body });

            onSuccess?.();
            onClose?.();
        } catch (error) {
            console.error('Erro ao atualizar parâmetros do contrato:', error);
            if (actions && actions.setErrors) {
                actions.setErrors({ submit: error.message });
            }
        } finally {
            if (actions && actions.setSubmitting) {
                actions.setSubmitting(false);
            }
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                Editar Parâmetros do Contrato
            </DialogTitle>
            <Formik
                initialValues={getInitialValues()}
                validationSchema={validationSchema}
                enableReinitialize={true}
                onSubmit={handleFormSubmit}
            >
                {({ values, setFieldValue, isSubmitting, errors, touched, actions }) => (
                    <Form>
                        <DialogContent>
                            <Stack spacing={3}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Configure os parâmetros financeiros do contrato
                                </Typography>

                                <TextField
                                    label="Juros de mora (atraso parcela) %"
                                    type="number"
                                    fullWidth
                                    value={values.latePaymentInterest * 100}
                                    onChange={(e) => {
                                        const value = Number(e.target.value) / 100;
                                        setFieldValue('latePaymentInterest', value);
                                    }}
                                    error={touched.latePaymentInterest && Boolean(errors.latePaymentInterest)}
                                    helperText={
                                        (touched.latePaymentInterest && errors.latePaymentInterest) ||
                                        "Ex: 100 para 100%"
                                    }
                                />

                                <TextField
                                    label="Multa de mora (atraso parcela) %"
                                    type="number"
                                    fullWidth
                                    value={values.latePaymentFine * 100}
                                    onChange={(e) => {
                                        const value = Number(e.target.value) / 100;
                                        setFieldValue('latePaymentFine', value);
                                    }}
                                    error={touched.latePaymentFine && Boolean(errors.latePaymentFine)}
                                    helperText={
                                        (touched.latePaymentFine && errors.latePaymentFine) ||
                                        "Ex: 100 para 100%"
                                    }
                                />

                                <TextField
                                    label="Dias de carência (atraso parcelas)"
                                    type="number"
                                    fullWidth
                                    value={values.installmentDelayDays}
                                    onChange={(e) => {
                                        const value = Number(e.target.value);
                                        setFieldValue('installmentDelayDays', value);
                                    }}
                                    error={touched.installmentDelayDays && Boolean(errors.installmentDelayDays)}
                                    helperText={
                                        (touched.installmentDelayDays && errors.installmentDelayDays) ||
                                        "Número de dias de carência para parcelas em atraso"
                                    }
                                />

                                {errors.submit && (
                                    <Typography color="error" variant="body2">
                                        {errors.submit}
                                    </Typography>
                                )}
                            </Stack>
                        </DialogContent>

                        <DialogActions>
                            <Button onClick={onClose} color="inherit">
                                Cancelar
                            </Button>
                            <LoadingButton
                                type="submit"
                                variant="contained"
                                color="primary"
                                loading={isSubmitting}
                                disabled={isSubmitting}
                                onClick={() => handleFormSubmit(values, actions)}
                            >
                                Salvar Alterações
                            </LoadingButton>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
}